/**
 * CSVテンプレート管理画面
 *
 * 管理者がシステム共通のCSVテンプレートを管理する画面
 */

import { useState, useEffect, useCallback } from "react";
import {
  Container,
  Title,
  Stack,
  Button,
  Group,
  Modal,
  Text,
  Alert,
} from "@mantine/core";
import { showSuccess, showError } from "../utils/notifications";
import { Plus, LayoutTemplate } from "lucide-react";
import {
  fetchCsvTemplates,
  createCsvTemplate,
  updateCsvTemplate,
  deleteCsvTemplate,
  CsvTemplateCommon,
} from "../services/csvSettingsService";
import { TemplateEditModal, TemplateFormData } from "../components/csv/TemplateEditModal";
import { TemplateTable } from "../components/csv/TemplateTable";

export function CsvTemplatesPage() {
  const [templates, setTemplates] = useState<CsvTemplateCommon[]>([]);
  const [loading, setLoading] = useState(true);

  // モーダル状態
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<CsvTemplateCommon | null>(null);
  const [saving, setSaving] = useState(false);

  // 削除確認
  const [deleteTarget, setDeleteTarget] = useState<CsvTemplateCommon | null>(null);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchCsvTemplates();
      setTemplates(data);
    } catch (error) {
      console.error(error);
      showError("テンプレートの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // 新規作成モード
  const handleOpenCreate = () => {
    setEditingTemplate(null);
    setModalOpen(true);
  };

  // 編集モード
  const handleOpenEdit = (template: CsvTemplateCommon) => {
    setEditingTemplate(template);
    setModalOpen(true);
  };

  // 保存処理
  const handleSave = async (data: TemplateFormData) => {
    if (!data.name) {
      showError("テンプレート名は必須です");
      return;
    }

    try {
      setSaving(true);
      const input = {
        name: data.name,
        description: data.description,
        encoding: data.encoding,
        columns: data.columns.map((c, i) => ({ ...c, order: i + 1 })),
        promptAddition: data.promptAddition,
      };

      if (editingTemplate) {
        await updateCsvTemplate(editingTemplate.id, input);
        showSuccess("テンプレートを更新しました");
      } else {
        await createCsvTemplate(input);
        showSuccess("テンプレートを作成しました");
      }

      setModalOpen(false);
      await loadTemplates();
    } catch (error) {
      console.error(error);
      showError(`保存に失敗しました: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setSaving(false);
    }
  };

  // 削除処理
  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      setSaving(true);
      await deleteCsvTemplate(deleteTarget.id);
      showSuccess("テンプレートを削除しました");
      setDeleteTarget(null);
      await loadTemplates();
    } catch (error) {
      console.error(error);
      showError("削除に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container size="lg">
      {/* AIアシスタント向けガイド */}
      <div data-ai-guide hidden>
        {`[ページ: CSVテンプレート管理]
[概要: OCR結果をCSV出力する際の列定義（テンプレート）を管理する画面]
[登録テンプレート数: ${templates.length}件]
[テンプレートの構成要素]
- テンプレート名: 識別用の名前
- 説明: 用途の説明
- 文字コード: UTF-8(BOM付) または Shift_JIS
- 列定義: 出力するCSVのヘッダー名と順序
- 追加プロンプト: AI読み取り時の追加指示（任意）
[ダイアログ]
- 編集ダイアログ: ${modalOpen ? "表示中" : "非表示"} (${editingTemplate ? "編集" : "新規作成"}モード)
- 削除確認ダイアログ: ${deleteTarget ? "表示中" : "非表示"}
[文字コードの選び方]
- Shift_JIS: Excelで直接開く場合、古い基幹システム向け
- UTF-8: 新しいシステム、Webサービス連携向け
[操作のヒント]
- テンプレートは顧客ごとに設定で紐づけて使用する
- 行をクリックするとテンプレートを編集可能
- 列の順序はドラッグではなく↑↓ボタンで変更`}
      </div>

      <Stack gap="lg">
        <Group justify="space-between">
          <Group>
            <LayoutTemplate size={28} />
            <Title order={1}>CSVテンプレート管理</Title>
          </Group>
          <Button leftSection={<Plus size={16} />} onClick={handleOpenCreate}>
            テンプレート作成
          </Button>
        </Group>

        <TemplateTable templates={templates} loading={loading} onSelect={handleOpenEdit} />
      </Stack>

      {/* 編集モーダル */}
      <TemplateEditModal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        template={editingTemplate}
        onSave={handleSave}
        onDelete={setDeleteTarget}
        saving={saving}
      />

      {/* 削除確認モーダル */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="テンプレート削除">
        <Stack>
          <Text>
            <strong>{deleteTarget?.name}</strong> を削除しますか？
          </Text>
          <Alert color="red">
            このテンプレートを使用している顧客設定には影響しませんが、テンプレート選択肢からは消えます。
          </Alert>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteTarget(null)}>
              キャンセル
            </Button>
            <Button color="red" onClick={handleDelete} loading={saving}>
              削除
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
